export const BLIND_TYPE_POSITIVE = 'positive'
export const BLIND_TYPE_NEGATIVE = 'negative'
export const DEFAULT_BLIND_NEGATIVE_CAP = 999

export const hasValue = (value) =>
  value !== null && value !== undefined && value !== ''

export const toOptionalNumber = (value) => {
  if (!hasValue(value)) {
    return null
  }

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : null
}

const normalizeCountValue = (value) => {
  const parsedValue = toOptionalNumber(value)
  if (parsedValue === null) {
    return null
  }

  return Math.max(parsedValue, 0)
}

export const resolveBlindTypeFromPrices = ({ blindPrice, groupPrice }) => {
  const parsedBlindPrice = toOptionalNumber(blindPrice)
  const parsedGroupPrice = toOptionalNumber(groupPrice)

  if (
    parsedBlindPrice === null ||
    parsedGroupPrice === null ||
    parsedBlindPrice === parsedGroupPrice
  ) {
    return null
  }

  return parsedBlindPrice > parsedGroupPrice
    ? BLIND_TYPE_POSITIVE
    : BLIND_TYPE_NEGATIVE
}

export const resolveBlindTypeFromCar = (car = {}) => {
  if (
    car?.blindType === BLIND_TYPE_POSITIVE ||
    car?.blindType === BLIND_TYPE_NEGATIVE
  ) {
    return car.blindType
  }

  return resolveBlindTypeFromPrices({
    blindPrice: car?.blindPrice ?? car?.reprice,
    groupPrice: car?.price,
  })
}

const toCapValue = (value, fallback = null) => {
  const parsedValue = normalizeCountValue(value)
  if (parsedValue !== null) {
    return parsedValue
  }

  return fallback
}

const isSet = (value) => value !== undefined && value !== null

const resolveBlindPositiveCap = ({ caps = {}, defaultPositiveCap = 0 } = {}) =>
  toCapValue(
    caps?.blindPositiveCap ?? caps?.blind_positive_cap ?? caps?.firstChoice,
    defaultPositiveCap
  )

const resolveBlindNegativeCap = ({
  caps = {},
  defaultNegativeCap = DEFAULT_BLIND_NEGATIVE_CAP,
} = {}) => {
  const explicitBlindNegativeCap =
    caps?.blindNegativeCap ?? caps?.blind_negative_cap

  const legacySecondChoice = caps?.secondChoice ?? caps?.second_choice

  // secondChoice=0 with blindNegativeCap=999 (legacy stored default) means explicit zero
  if (
    legacySecondChoice === 0 &&
    (!isSet(explicitBlindNegativeCap) ||
      explicitBlindNegativeCap === DEFAULT_BLIND_NEGATIVE_CAP)
  ) {
    return 0
  }

  if (isSet(explicitBlindNegativeCap)) {
    return toCapValue(explicitBlindNegativeCap, defaultNegativeCap)
  }

  return toCapValue(legacySecondChoice, defaultNegativeCap)
}

export const getBlindOfferCapsByType = ({
  caps = {},
  defaultPositiveCap = 0,
  defaultNegativeCap = DEFAULT_BLIND_NEGATIVE_CAP,
} = {}) => ({
  positive: resolveBlindPositiveCap({ caps, defaultPositiveCap }),
  negative: resolveBlindNegativeCap({ caps, defaultNegativeCap }),
})

export const getBlindOffersUsedByType = (cars = []) =>
  cars.reduce(
    (accumulator, car) => {
      const blindPrice = car?.blindPrice ?? car?.reprice
      if (!hasValue(blindPrice)) {
        return accumulator
      }

      const blindType = resolveBlindTypeFromCar(car)
      if (blindType === BLIND_TYPE_POSITIVE) {
        accumulator.positive += 1
      } else if (blindType === BLIND_TYPE_NEGATIVE) {
        accumulator.negative += 1
      }

      return accumulator
    },
    { positive: 0, negative: 0 }
  )

const getRemainingValue = (cap, used) => {
  if (cap === null || cap === undefined) {
    return null
  }

  return Math.max(cap - used, 0)
}

export const getBlindOffersRemainingByType = ({
  caps = {},
  cars = [],
  defaultPositiveCap = 0,
  defaultNegativeCap = DEFAULT_BLIND_NEGATIVE_CAP,
} = {}) => {
  const used = getBlindOffersUsedByType(cars)
  const capByType = getBlindOfferCapsByType({
    caps,
    defaultPositiveCap,
    defaultNegativeCap,
  })

  return {
    positive: getRemainingValue(capByType.positive, used.positive),
    negative: getRemainingValue(capByType.negative, used.negative),
  }
}

export const normalizeBlindOffersRemaining = (
  remaining,
  defaultNegativeCap = DEFAULT_BLIND_NEGATIVE_CAP
) => {
  if (remaining === null || remaining === undefined) {
    return { positive: null, negative: null }
  }

  if (typeof remaining === 'number' || typeof remaining === 'string') {
    const parsedValue = normalizeCountValue(remaining)
    return {
      positive: parsedValue,
      negative: parsedValue,
    }
  }

  if (typeof remaining === 'object') {
    const legacyRemaining = normalizeCountValue(
      remaining?.remaining ?? remaining?.count ?? remaining?.total
    )
    const positive = normalizeCountValue(
      remaining?.positive ??
        remaining?.blindPositive ??
        remaining?.blindPositiveOffersRemaining ??
        legacyRemaining
    )
    const negative = normalizeCountValue(
      remaining?.negative ??
        remaining?.blindNegative ??
        remaining?.blindNegativeOffersRemaining ??
        legacyRemaining ??
        defaultNegativeCap
    )

    return { positive, negative }
  }

  return { positive: null, negative: null }
}

export const hasRemainingBlindOffersForType = (remaining, blindType) => {
  const normalizedRemaining = normalizeBlindOffersRemaining(remaining)
  const remainingValue =
    blindType === BLIND_TYPE_NEGATIVE
      ? normalizedRemaining.negative
      : normalizedRemaining.positive

  return (
    remainingValue === null ||
    remainingValue === undefined ||
    Number(remainingValue) > 0
  )
}

export const hasAnyRemainingBlindOffers = (remaining) =>
  hasRemainingBlindOffersForType(remaining, BLIND_TYPE_POSITIVE) ||
  hasRemainingBlindOffersForType(remaining, BLIND_TYPE_NEGATIVE)
